import React, { useState } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { useSubmitFeedback, useMyFeedback } from '../../hooks/use-feedback';
import { useFeatureFlag } from '../../hooks/use-feature-flag';
import type { FeedbackReason, FeedbackTargetKind, InlineCardTargetRef, InsightTargetRef } from '../../api/feedback';

const GRAD_ID = 'feedback-btn-grad';

const GradientDefs = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }}>
    <defs>
      <linearGradient id={GRAD_ID} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="var(--accent)" />
        <stop offset="100%" stopColor="var(--accent)" />
      </linearGradient>
    </defs>
  </svg>
);

const ThumbUpIcon = ({ filled }: { filled: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill={filled ? `url(#${GRAD_ID})` : 'none'} stroke={`url(#${GRAD_ID})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 10v12"/>
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>
  </svg>
);

const ThumbDownIcon = ({ filled }: { filled: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill={filled ? `url(#${GRAD_ID})` : 'none'} stroke={`url(#${GRAD_ID})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 14V2"/>
    <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/>
  </svg>
);

const REASONS: Array<{ value: FeedbackReason; label: string }> = [
  { value: 'incorrect', label: 'Incorrect' },
  { value: 'missing-info', label: 'Missing info' },
  { value: 'slow', label: 'Too slow' },
  { value: 'wrong-tool', label: 'Wrong tool chosen' },
];

export interface FeedbackButtonsProps {
  targetKind: FeedbackTargetKind;
  threadId?: string | null;
  messageId?: string | null;
  targetRef?: InlineCardTargetRef | InsightTargetRef | null;
  snapshot: Record<string, unknown>;
}

const iconStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 4px',
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
};

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  targetKind,
  threadId,
  messageId,
  targetRef,
  snapshot,
}) => {
  const enabled = useFeatureFlag('ai.core.feedback.enabled');
  const submit = useSubmitFeedback();
  const cardRef = targetRef && 'pluginId' in targetRef ? targetRef : undefined;
  const existing = useMyFeedback({
    targetKind,
    threadId: threadId ?? undefined,
    messageId: messageId ?? undefined,
    pluginId: cardRef?.pluginId,
    entityType: cardRef?.entityType,
    entityId: cardRef?.entityId,
    enabled,
  });

  const [downOpen, setDownOpen] = useState(false);
  const [reasons, setReasons] = useState<FeedbackReason[]>([]);
  const [freeText, setFreeText] = useState('');
  const [justSubmitted, setJustSubmitted] = useState(false);

  if (!enabled) return null;

  const pressed = existing.data?.feedback?.sentiment;

  const submitUp = () => {
    submit.mutate(
      {
        threadId: threadId ?? null,
        messageId: messageId ?? null,
        targetKind,
        targetRef: targetRef ?? null,
        sentiment: 'up',
        snapshot,
      },
      {
        onSuccess: () => {
          setJustSubmitted(true);
          setTimeout(() => setJustSubmitted(false), 2_000);
        },
      }
    );
  };

  const submitDown = () => {
    submit.mutate(
      {
        threadId: threadId ?? null,
        messageId: messageId ?? null,
        targetKind,
        targetRef: targetRef ?? null,
        sentiment: 'down',
        reasons: reasons.length ? reasons : undefined,
        freeText: freeText.trim() || undefined,
        snapshot,
      },
      {
        onSuccess: () => {
          setDownOpen(false);
          setReasons([]);
          setFreeText('');
          setJustSubmitted(true);
          setTimeout(() => setJustSubmitted(false), 2_000);
        },
      }
    );
  };

  const toggleReason = (r: FeedbackReason) =>
    setReasons((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'inherit' }}>
      <GradientDefs />
      <button
        aria-label="Mark helpful"
        aria-pressed={pressed === 'up'}
        onClick={submitUp}
        style={iconStyle}
      >
        <ThumbUpIcon filled={pressed === 'up'} />
      </button>
      <RPopover.Root open={downOpen} onOpenChange={setDownOpen}>
        <RPopover.Trigger asChild>
          <button
            aria-label="Mark unhelpful"
            aria-pressed={pressed === 'down'}
            style={iconStyle}
          >
            <ThumbDownIcon filled={pressed === 'down'} />
          </button>
        </RPopover.Trigger>
        <RPopover.Portal>
          <RPopover.Content
            sideOffset={6}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: 10,
              width: 260,
              fontSize: 12,
              boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
              zIndex: 50,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>What went wrong?</div>
            {REASONS.map((r) => (
              <label
                key={r.value}
                style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={reasons.includes(r.value)}
                  onChange={() => toggleReason(r.value)}
                />
                <span>{r.label}</span>
              </label>
            ))}
            <textarea
              aria-label="Additional details"
              placeholder="Optional: anything else we should know?"
              value={freeText}
              onChange={(e) => setFreeText(e.target.value.slice(0, 500))}
              style={{
                width: '100%',
                marginTop: 6,
                marginBottom: 6,
                fontSize: 11,
                padding: 4,
                border: '1px solid var(--border)',
                borderRadius: 4,
                background: 'var(--surface)',
                color: 'var(--foreground)',
              }}
              rows={3}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
              <button onClick={() => setDownOpen(false)} style={{ ...iconStyle, fontSize: 11 }}>
                Cancel
              </button>
              <button
                onClick={submitDown}
                style={{ ...iconStyle, fontSize: 11, color: 'var(--primary)', fontWeight: 600 }}
              >
                Send
              </button>
            </div>
          </RPopover.Content>
        </RPopover.Portal>
      </RPopover.Root>
      {justSubmitted && (
        <span style={{ fontSize: 10, background: 'linear-gradient(135deg, var(--accent), var(--accent))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
          Thanks — noted
        </span>
      )}
    </span>
  );
};
